import { useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, keys, queryClient, useMe } from '../api.ts';
import type { CompetitionSummary, ReportListItem } from '../../shared/types.ts';
import { Loading } from './ui.tsx';

function Logo() {
  return (
    <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden>
      <rect width="32" height="32" rx="8" fill="#18222b" stroke="#2a3947" />
      <path d="M6 22c3-8 7-12 10-12s7 4 10 12" fill="none" stroke="#2ec4b6" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M10 22c2-4.5 4-6.5 6-6.5s4 2 6 6.5" fill="none" stroke="#88c0d0" strokeWidth="1.6" strokeLinecap="round" opacity=".7" />
      <circle cx="16" cy="10" r="2.4" fill="#2ec4b6" />
    </svg>
  );
}

export function Sidebar({ open, onNavigate }: { open: boolean; onNavigate: () => void }) {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const reports = useQuery({ queryKey: keys.reports, queryFn: () => api.get<ReportListItem[]>('/reports') });
  const comps = useQuery({ queryKey: keys.competitions, queryFn: () => api.get<CompetitionSummary[]>('/competitions') });
  const logout = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login');
    },
  });

  return (
    <aside className={`sidebar${open ? ' open' : ''}`} onClick={(e) => (e.target as HTMLElement).closest('a') && onNavigate()}>
      <Link to={me ? `/users/${me.id}` : '/'} className="brand">
        <Logo />
        <span>
          DXA.fi
          <small>OH DX -taulukot</small>
        </span>
      </Link>

      {me && (
        <div className="nav-section">
          <h4>Käyttäjät</h4>
          <ul>
            <li>
              <NavLink to={`/users/${me.id}`} end>
                Omat tiedot
              </NavLink>
            </li>
            <li>
              <NavLink to={`/users/${me.id}/qsos`}>Omat QSO:t</NavLink>
            </li>
            <li>
              <NavLink to="/import/single">Lisää QSO</NavLink>
            </li>
            <li>
              <NavLink to="/import/adif">Massalisäys (ADIF)</NavLink>
            </li>
            <li>
              <NavLink to="/users" end>
                Kaikki käyttäjät
              </NavLink>
            </li>
            {me.isAdmin && (
              <li>
                <NavLink to="/users/new">Lisää käyttäjä</NavLink>
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="nav-section">
        <h4>Taulukot</h4>
        <ul>
          {me && (
            <li>
              <NavLink to="/reports/search">Haku</NavLink>
            </li>
          )}
          {reports.data?.map((r) => (
            <li key={r.id}>
              <NavLink to={`/reports/${r.id}`} title={r.name}>
                {r.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="nav-section">
        <h4>Kilpailut</h4>
        <ul>
          {comps.data?.slice(0, 8).map((c) => (
            <li key={c.id}>
              <NavLink to={`/competitions/${c.id}`} title={c.heading}>
                {c.heading}
              </NavLink>
            </li>
          ))}
          <li>
            <NavLink to="/competitions" end>
              {me?.isAdmin ? 'Kaikki / muokkaa' : 'Kaikki kilpailut'}
            </NavLink>
          </li>
        </ul>
      </div>

      {me?.isAdmin && (
        <div className="nav-section">
          <h4>Ylläpito</h4>
          <ul>
            <li>
              <NavLink to="/admin/countries">Maat</NavLink>
            </li>
            <li>
              <NavLink to="/admin/bands">Bandit ja moodit</NavLink>
            </li>
            <li>
              <NavLink to="/admin/help">Ohjetekstit</NavLink>
            </li>
            <li>
              <NavLink to="/admin/settings">Asetukset ja varmuuskopio</NavLink>
            </li>
          </ul>
        </div>
      )}

      <div className="sidebar-footer">
        {me ? (
          <div className="row">
            <span>
              <span className="call">{me.id}</span>
              <br />
              <span className="faint small">{me.name}</span>
            </span>
            <span className="spacer" />
            <button className="btn btn-sm" onClick={() => logout.mutate()}>
              Kirjaudu ulos
            </button>
          </div>
        ) : (
          <div className="row">
            <Link className="btn btn-primary btn-sm" to="/login">
              Kirjaudu sisään
            </Link>
            <Link className="btn btn-sm" to="/register">
              Rekisteröidy
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}

export function AppLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="app">
      <Sidebar open={open} onNavigate={() => setOpen(false)} />
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <div style={{ minWidth: 0 }}>
        <div className="mobile-bar">
          <button className="btn btn-sm" onClick={() => setOpen(true)} aria-label="Valikko">
            ☰
          </button>
          <strong>DXA.fi</strong>
        </div>
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Ohjaa kirjautumattomat kirjautumissivulle */
export function RequireAuth({ admin = false }: { admin?: boolean }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  // Välitetään vanhemman reitin konteksti (esim. UserLayout) eteenpäin lapsireiteille
  const parentContext = useOutletContext();
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (admin && !me.isAdmin) return <div className="alert alert-error">Tämä sivu on vain ylläpitäjille.</div>;
  return <Outlet context={parentContext} />;
}

export function HomeRedirect() {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <Loading />;
  return <Navigate to={me ? `/users/${encodeURIComponent(me.id)}` : '/login'} replace />;
}
